// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useOutletContext, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import { shallow } from 'zustand/shallow';

import helpers from '@/utils/helpers';
import getLocationDisplayName, { flattenLocationsForSelect } from '@staty/shared/utils/getLocationDisplayName';
import useDivisionStore from '@store/divisionStore';
import useTeamDivisionStore from '@store/teamDivisionStore';
import useLeagueStore from '@store/leagueStore';
import useLocationStore from '@store/locationStore';
import useGameStore from '@store/gameStore';
import useTournamentStore from '@store/tournamentStore';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Upload, Download, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';

import { parseGamesCsv, buildTemplateCsv } from './bulkImport/parseGamesCsv';
import { resolveGameRow, flagDuplicates } from './bulkImport/resolveGameRow';
import type { ResolvedGameRow } from './bulkImport/types';
import BulkImportPreviewCalendar from './components/BulkImportPreviewCalendar';

export default function TournamentBulkImportGames() {
  const { tournamentId } = useParams();
  const { leagueName, league: organization } = useOutletContext() as any;
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<ResolvedGameRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // -- Data stores --
  const { tournaments, getTournamentById } = useTournamentStore((state: any) => ({
    tournaments: state.tournaments,
    getTournamentById: state.getTournamentById,
  }), shallow);
  const { divisions, getActiveDivisionsByTournamentId } = useDivisionStore();
  const { teamDivisions, getTeamDivisionsByDivisionId } = useTeamDivisionStore();
  const { seasons, getContestsByTournamentId } = useLeagueStore((state: any) => ({
    seasons: state.leagues,
    getContestsByTournamentId: state.getContestsByTournamentId,
  }), shallow);
  const { locations, getLocationsByLeagueName } = useLocationStore((state: any) => ({
    locations: state.locations,
    getLocationsByLeagueName: state.getLocationsByLeagueName,
  }), shallow);
  const addBulkGamesFn = useGameStore((state: any) => state.addBulkGames);
  const addBulkGames = useMemo(() => addBulkGamesFn(getToken), [addBulkGamesFn, getToken]);

  useEffect(() => {
    if (tournamentId) {
      getTournamentById(tournamentId);
      getContestsByTournamentId(tournamentId);
      getActiveDivisionsByTournamentId(tournamentId);
    }
  }, [tournamentId, getTournamentById, getContestsByTournamentId, getActiveDivisionsByTournamentId]);

  const tournament = useMemo(
    () => tournaments.find((t: any) => t._id === tournamentId),
    [tournaments, tournamentId]
  );

  useEffect(() => {
    if (leagueName) getLocationsByLeagueName(leagueName, { includeSubVenues: true });
  }, [leagueName, getLocationsByLeagueName]);

  useEffect(() => {
    divisions.forEach((division: any) => {
      getTeamDivisionsByDivisionId(division._id);
    });
  }, [divisions, getTeamDivisionsByDivisionId]);

  // Tournament divisions only. A division belongs to a section (league), and
  // the section belongs to a tournament — divisions don't carry a `tournament`
  // ref directly. Both the divisions and sections stores accumulate across
  // tournaments, so we filter through the section set for the current tournament.
  const tournamentDivisions = useMemo(() => {
    const sectionIds = new Set(
      seasons
        .filter((s: any) => helpers.getObjId(s.tournament) === tournamentId)
        .map((s: any) => s._id)
    );
    if (sectionIds.size === 0) return [];
    return divisions.filter((d: any) => sectionIds.has(helpers.getObjId(d.league)));
  }, [divisions, seasons, tournamentId]);

  const organizationId = useMemo(
    () => helpers.getObjId(tournamentDivisions[0]?.organization) || null,
    [tournamentDivisions]
  );

  // Flatten venues + sub-venues into a single searchable list so a CSV value
  // like "1" (a sub-venue / court name) can match the sub-venue doc, and a
  // value like "North High - 1" can match its `displayName`.
  const flatLocations = useMemo(() => flattenLocationsForSelect(locations), [locations]);

  const resolveContext = useMemo(
    () => ({
      organizationId,
      orgSport: organization?.sports?.[0],
      divisions: tournamentDivisions,
      teamDivisions,
      sections: seasons,
      locations: flatLocations,
    }),
    [organizationId, organization, tournamentDivisions, teamDivisions, seasons, flatLocations]
  );

  // -- CSV handling --
  const handleFile = useCallback(
    async (file: File) => {
      setFileName(file.name);
      const { rows: rawRows, unknownHeaders: uh, parseErrors: pe } = await parseGamesCsv(file);
      setUnknownHeaders(uh);
      setParseErrors(pe);
      const resolved = rawRows.map((r) => resolveGameRow(r, resolveContext));
      setRows(flagDuplicates(resolved));
    },
    [resolveContext]
  );

  // Re-resolve when context updates after the file was already picked.
  useEffect(() => {
    if (rows.length === 0) return;
    setRows((prev) => flagDuplicates(prev.map((r) => resolveGameRow(r.raw, resolveContext))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveContext.divisions, resolveContext.teamDivisions, resolveContext.sections, resolveContext.locations]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = ''; // allow re-uploading the same filename
  };

  const handleDownloadTemplate = () => {
    const csv = buildTemplateCsv(
      tournamentDivisions[0]?.name || 'Division Name',
      locations[0]?.name || locations[0]?.displayName || 'Location Name'
    );
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-import-games-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // -- Summary --
  const summary = useMemo(() => {
    let ready = 0;
    let warning = 0;
    let error = 0;
    rows.forEach((r) => {
      if (r.status === 'ready') ready++;
      else if (r.status === 'warning') warning++;
      else error++;
    });
    return { ready, warning, error, total: rows.length };
  }, [rows]);

  const saveable = summary.ready + summary.warning;

  // -- Save --
  const handleSave = async () => {
    const divisionsById = new Map(tournamentDivisions.map((d: any) => [d._id, d]));
    const locationsById = new Map(flatLocations.map((l: any) => [l._id, l]));

    const payloads = rows
      .filter((r) => r.status !== 'error')
      .map((r) => {
        const division = divisionsById.get(r.payload.division as string);
        const locationDoc = r.payload.location ? locationsById.get(r.payload.location) : null;
        const sport = Array.isArray(division?.sports) && division.sports.length > 0 ? division.sports[0] : 'basketball';
        const payload: any = {
          division: r.payload.division,
          // Existing live create-game flow sends both league and a legacy `season`
          // field; bulkCreate uses insertMany which bypasses pre-save hooks, so
          // we mirror the live shape to keep downstream consumers consistent.
          season: r.payload.league,
          league: r.payload.league,
          organization: r.payload.organization,
          homeTeam: r.payload.homeTeam,
          awayTeam: r.payload.awayTeam,
          homeScore: '',
          awayScore: '',
          globalDate: r.payload.globalDate,
          date: r.payload.globalDate,
          time: r.payload.time,
          duration: r.payload.duration,
          sport,
          locationModel: r.payload.location || undefined,
          location: locationDoc ? getLocationDisplayName(locationDoc) : (r.payload.locationName || ''),
        };
        // Placement games: no team refs, the label carried on the placeholder
        // fields, and playoffRound REQUIRED (the public standings page splits
        // regular vs playoff games on it — see resolveGameRow).
        if (r.payload.homeTeamPlaceholder || r.payload.awayTeamPlaceholder) {
          payload.homeTeamPlaceholder = r.payload.homeTeamPlaceholder || '';
          payload.awayTeamPlaceholder = r.payload.awayTeamPlaceholder || '';
          payload.playoffRound = r.payload.playoffRound || null;
          payload.gameLabel = r.payload.gameLabel || '';
        }
        return payload;
      });

    if (payloads.length === 0) {
      toast.error('Nothing to import. Every row has errors.');
      return;
    }

    setIsSaving(true);
    try {
      // addBulkGames never rejects — it returns { error } on failure. Check it:
      // trusting the resolved promise alone once turned a failed 848-game
      // import into a success toast + navigation.
      const res = await addBulkGames(payloads);
      if (res?.error) {
        console.error('Bulk import failed', res.error);
        toast.error(`Bulk import failed: ${res.error}`);
        return;
      }
      toast.success(`Imported ${payloads.length} game${payloads.length === 1 ? '' : 's'}`);
      navigate(`/admin/scheduler-day2/tournament/${tournamentId}`);
    } catch (err) {
      console.error('Bulk import failed', err);
      toast.error('Bulk import failed. Check the console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Bulk import games"
        description={tournament?.name}
        actions={
          <Link to={`/admin/scheduler-day2/tournament/${tournamentId}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to schedule
            </Button>
          </Link>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Upload CSV</CardTitle>
          <CardDescription>
            Upload a CSV of games to preview before importing. Admins type names, not IDs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" /> Choose CSV
            </Button>
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="mr-2 h-4 w-4" /> Download template
            </Button>
            {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
          </div>

          {unknownHeaders.length > 0 && (
            <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
              Ignored unknown columns: {unknownHeaders.join(', ')}
            </div>
          )}
          {parseErrors.length > 0 && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <div className="font-semibold">CSV parse warnings</div>
              <ul className="list-disc pl-5">
                {parseErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-4 py-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 font-medium text-success">
                <CheckCircle className="h-4 w-4" /> {summary.ready} ready
              </span>
              <span className="inline-flex items-center gap-1.5 font-medium text-warning">
                <AlertTriangle className="h-4 w-4" /> {summary.warning} warning{summary.warning === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1.5 font-medium text-destructive">
                <XCircle className="h-4 w-4" /> {summary.error} error{summary.error === 1 ? '' : 's'}
              </span>
              <span className="text-muted-foreground">of {summary.total} total</span>
            </div>
            <Button onClick={handleSave} disabled={saveable === 0 || isSaving}>
              {isSaving ? 'Importing…' : `Import ${saveable} game${saveable === 1 ? '' : 's'}`}
            </Button>
          </div>

          <Tabs defaultValue="list">
            <TabsList>
              <TabsTrigger value="list">List</TabsTrigger>
              <TabsTrigger value="calendar">Calendar</TabsTrigger>
            </TabsList>

            <TabsContent value="list" className="mt-4">
              <div className="rounded-md border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead className="w-28">Status</TableHead>
                      <TableHead>Division</TableHead>
                      <TableHead>Home Team</TableHead>
                      <TableHead>Away Team</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.raw.rowIndex} className={row.status === 'error' ? 'bg-destructive/5' : row.status === 'warning' ? 'bg-warning/5' : undefined}>
                        <TableCell className="text-xs text-muted-foreground">{row.raw.rowIndex}</TableCell>
                        <TableCell>
                          <StatusBadge status={row.status} />
                        </TableCell>
                        <TableCell>{row.display.divisionName || <em className="text-muted-foreground/60">—</em>}</TableCell>
                        {/* Placement rows carry a label, not a linked team —
                            style them so an admin can tell at a glance that no
                            team ref will be written. */}
                        <TableCell className={row.display.isPlaceholder ? 'italic text-primary' : undefined}>
                          {row.display.homeTeamName || <em className="text-muted-foreground/60">—</em>}
                        </TableCell>
                        <TableCell className={row.display.isPlaceholder ? 'italic text-primary' : undefined}>
                          {row.display.awayTeamName || <em className="text-muted-foreground/60">—</em>}
                        </TableCell>
                        <TableCell className="text-sm">{row.display.dateKey || row.raw.Date || <em className="text-muted-foreground/60">—</em>}</TableCell>
                        <TableCell className="text-sm">{row.payload.time || row.raw.Time || <em className="text-muted-foreground/60">—</em>}</TableCell>
                        <TableCell className="text-sm">{row.display.locationName || <em className="text-muted-foreground/60">—</em>}</TableCell>
                        <TableCell>
                          {row.issues.length === 0 ? (
                            <span className="text-xs text-muted-foreground/60">—</span>
                          ) : (
                            <ul className="space-y-0.5">
                              {row.issues.map((issue, i) => (
                                <li
                                  key={i}
                                  className={`text-xs ${
                                    issue.severity === 'error' ? 'text-destructive' : 'text-warning'
                                  }`}
                                >
                                  {issue.message}
                                </li>
                              ))}
                            </ul>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="calendar" className="mt-4">
              <BulkImportPreviewCalendar rows={rows} locations={flatLocations} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </PageContainer>
  );
}

function StatusBadge({ status }: { status: 'ready' | 'warning' | 'error' }) {
  if (status === 'ready') {
    return (
      <Badge className="bg-success/10 text-success hover:bg-success/10">
        <CheckCircle className="mr-1 h-3 w-3" /> Ready
      </Badge>
    );
  }
  if (status === 'warning') {
    return (
      <Badge className="bg-warning/10 text-warning hover:bg-warning/10">
        <AlertTriangle className="mr-1 h-3 w-3" /> Warning
      </Badge>
    );
  }
  return (
    <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10">
      <XCircle className="mr-1 h-3 w-3" /> Error
    </Badge>
  );
}
